export enum ECalendarConnectionStatus {
  CONNECTED = "connected",
  NEEDS_RECONNECT = "needs_reconnect",
}

// NOTE: Unified calendar connection summary — matches backend CalendarConnectionEntity
export interface ICalendarConnectionSummary {
  uuid: string;
  provider: string;
  email: string | null;
  calendarLink: string | null;
  displayName: string | null;
  status: ECalendarConnectionStatus;
  color: string;
  createdAt: string;
}

export interface IGetCalendarsResponse {
  connections: ICalendarConnectionSummary[];
}

export interface IRedirectToGoogleParams {
  redirectTo: string;
}

export interface IRedirectToGoogleResponse {
  url: string;
}

export interface IRedirectToMicrosoftParams {
  redirectTo: string;
}

export interface IRedirectToMicrosoftResponse {
  url: string;
}

export interface ICreateCalendarLinkPayload {
  calendarLink: string;
  password?: string | null;
}

export interface IUpdateCalendarEventParams {
  calendarEventUuid: string;
  payload: IUpdateCalendarEventPayload;
}

export interface ICreateCalendarLinkResponse {
  uuid: string;
  calendarLink: string;
  createdAt: string;
}

export interface ICalendarEventItem {
  uuid: string;
  title: string;
  time: string;
  startTime: string;
  type: "meeting";
  language: "EN";
  toJoin: boolean;
  date: string;
  emails: string[] | null;
  color: string | null;
  provider: string | null;
}

export interface IGetCalendarEventsResponse {
  todayUpcomingMeetings: ICalendarEventItem[];
  tomorrowUpcomingMeetings: ICalendarEventItem[];
  tomorrowDate: string;
}

export interface IRefreshCalendarEventsResponse extends IGetCalendarEventsResponse {
  // NOTE: Number of calendar connections whose provider sync failed during
  // this refresh — > 0 means the meeting list may be partially stale.
  failedConnectionsCount: number;
}

export interface IUpdateCalendarColorPayload {
  uuid: string;
  color: string;
}

export interface IRenameCalendarPayload {
  uuid: string;
  displayName: string;
}

export interface IUpdateCalendarEventPayload {
  toJoin: boolean;
}

// NOTE: Calendar provider enum — matches backend ECalendarProvider
export enum EConnectedCalendarType {
  GOOGLE = "google",
  ICS = "ical",
  MICROSOFT = "microsoft",
  EXCHANGE = "exchange",
}

export interface IConnectedCalendar extends ICalendarConnectionSummary {
  type: EConnectedCalendarType;
}

export type TConnectedCalendar = IConnectedCalendar;

// NOTE: Enabled calendar providers — values match CALL_ASSISTANT_ENABLED_CALENDARS env var
export enum EEnabledCalendarProvider {
  GOOGLE = "google",
  OUTLOOK = "outlook",
  ICAL = "ical",
  EXCHANGE = "exchange",
}

export interface ICreateExchangeConnectionPayload {
  ewsUrl: string;
  ewsUsername: string;
  ewsPassword: string;
  ewsDomain?: string;
  displayName?: string;
}

export interface ICreateExchangeConnectionResponse {
  uuid: string;
  ewsUsername: string;
  createdAt: string;
}

export interface IGroupMeetingsByProviderProps {
  meetings: ICalendarEventItem[];
}

export interface ICalendarProviderGroup {
  provider: EConnectedCalendarType | string;
  meetings: ICalendarEventItem[];
}

export interface IGroupMeetingsByCalendarConnectionProps {
  meetings: ICalendarEventItem[];
  connections: ICalendarConnectionSummary[];
}

export interface ICalendarConnectionGroup {
  provider: EConnectedCalendarType | string;
  connection: ICalendarConnectionSummary | null;
  meetings: ICalendarEventItem[];
}
